import type { AnyEvent, ConnectivityType, EventBase } from '@snapchat/blizzard-schema';
import { dateToBlizzardTimestamp } from '../util/typeUtils';
import { getLogger } from '../logger';
import { getAppEnvironment } from '../environment';

const log = getLogger('eventMetadata');

export type EventName = AnyEvent['event_name'];

export interface NamedEvent {
  event_name: EventName;
}

/** Augments 'network-information-types' */
declare global {
  interface Navigator {
    connection: {
      downlink: number;
      type: 'bluetooth' | 'cellular' | 'wifi' | 'ethernet' | 'none';
    };
  }
}

/**
 * Metadata to attach to an individual event, rather than the whole frame. This may either be
 * specific to the individual event (like a name, or sampling rate) or frequently changing
 * information not appropriate to create a new frame for (e.g. connection bandwidth).
 *
 * @internal
 */
export type EventMetadata = Pick<
  EventBase,
  'device_connectivity' | 'connection_download_bandwidth_bps'
> &
  Required<Pick<EventBase, 'client_ts'>> &
  NamedEvent;

/**
 * Retrieve the {@link EventMetadata} for the given event.
 *
 * Note: network connection information is an experimental API, so this is best-effort support for
 * now. In particular, connectivity type is only available on Chrome OS.
 *
 * @internal
 */
export function getEventMetadata(eventName: EventName, clientTs: Date): EventMetadata {
  return {
    event_name: eventName,
    client_ts: dateToBlizzardTimestamp(clientTs),
    device_connectivity: getConnectivityType(),
    connection_download_bandwidth_bps: getBandwidthBps(),
  };
}

/** Get the current connectivity type. */
function getConnectivityType(): ConnectivityType | undefined {
  switch (self.navigator?.connection?.type) {
    case 'bluetooth':
      return 'BLUETOOTH';
    case 'cellular':
      return 'MOBILE';
    case 'wifi':
      return 'WIFI';
    case 'ethernet':
      return 'ETHERNET';
    case 'none':
      return 'UNREACHABLE';
    default:
      return undefined; // TODO: log
  }
}

/** Get the current bandwidth in bits per second. */
export function getBandwidthBps(): bigint | undefined {
  if (!self.navigator?.connection?.downlink) {
    return undefined;
  }
  // Convert from Mbps to bps
  return BigInt(Math.floor(self.navigator.connection.downlink * 1024 * 1024));
}

/** @internal */
export function getReferrer(): string | undefined {
  let referrer: string | undefined;
  if (document.referrer) {
    try {
      referrer = new URL(document.referrer).origin;
    } catch (e) {
      log.warn('Failed to parse referrer: ', document.referrer, e);
    }
  }
  return referrer;
}

/** @internal */
export function getAppBuild(): string | undefined {
  return getAppEnvironment().appBuild === 'PROD' ? '' : getAppEnvironment()?.appBuild;
}
